import useAppContext from 'flow/hooks/useAppContext';
import React, { useState, useRef, useEffect } from 'react';

interface CodeBlockWithCopyProps {
    code: string;
    idx?: number;
    inline?: boolean;
}

const CodeBlockWithCopy: React.FC<CodeBlockWithCopyProps> = ({
    code,
    idx,
    inline,
}) => {
    const [copied, setCopied] = useState(false);
    const { i18next } = useAppContext();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            // Cleanup timeout on unmount
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            isMountedRef.current = false;
        };
    }, []);

    const handleCopy = async () => {
        try {
            // Try modern clipboard API first
            await navigator.clipboard.writeText(code);
            if (isMountedRef.current) {
                setCopied(true);
                timeoutRef.current = setTimeout(() => {
                    if (isMountedRef.current) {
                        setCopied(false);
                    }
                }, 1500);
            }
        } catch {
            // Fallback to DOM-based copy for older browsers or permission issues
            try {
                const textarea = document.createElement('textarea');
                textarea.value = code;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                textarea.style.pointerEvents = 'none';
                document.body.appendChild(textarea);
                textarea.select();
                // eslint-disable-next-line deprecation/deprecation
                document.execCommand('copy');
                document.body.removeChild(textarea);

                if (isMountedRef.current) {
                    setCopied(true);
                    timeoutRef.current = setTimeout(() => {
                        if (isMountedRef.current) {
                            setCopied(false);
                        }
                    }, 1500);
                }
            } catch (fallbackError) {
                // Both methods failed - could show an error message to user
                console.warn('Copy to clipboard failed:', fallbackError);
            }
        }
    };

    if (inline) {
        return (
            <code
                key={`inline-code-${idx}`}
                className="mx-0.5 inline-block rounded bg-gray-200 px-1 py-0.5 font-mono text-sm"
            >
                {code}
            </code>
        );
    }

    return (
        <div
            key={idx !== undefined ? `code-wrap-${idx}` : undefined}
            className="group/copy relative"
        >
            <pre className="my-2 max-w-full overflow-x-auto rounded-lg bg-gray-300 p-4 font-mono text-xs whitespace-pre-wrap group-hover/copy:block">
                <code>{code}</code>
            </pre>
            <button
                onClick={handleCopy}
                title={
                    copied
                        ? i18next.t('snackbar.copied_to_clipboard')
                        : i18next.t('Tooltip.Title.Copy')
                }
                className="absolute end-2 top-2 cursor-pointer border-none bg-transparent p-1 text-lg opacity-0 transition-opacity group-hover/copy:opacity-100 focus:opacity-100 focus-visible:opacity-100"
                aria-label="Copy code"
                type="button"
            >
                <span className="inline-block">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <rect
                            x="5"
                            y="5"
                            width="10"
                            height="10"
                            rx="2"
                            fill="#888"
                        />
                        <rect
                            x="8"
                            y="2"
                            width="10"
                            height="10"
                            rx="2"
                            fill="#bbb"
                        />
                    </svg>
                </span>
                {copied && (
                    <span className="text-main absolute end-0 top-0 inline-block rounded p-1 text-xs">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <circle cx="8" cy="8" r="8" fill="currentColor" />
                            <path
                                d="M5 8.5l2 2 4-4"
                                stroke="#fff"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                )}
            </button>
        </div>
    );
};

export default CodeBlockWithCopy;
